import { Component } from '@theme/component';

/**
 * Engraving widget for product pages.
 *
 * Lives inside the product `<form>` so its named inputs are picked up by
 * FormData on AJAX cart submit. Uses native `<fieldset disabled>` semantics
 * (HTML spec) to exclude inactive controls from FormData rather than tracking
 * a parallel state object — disabled controls are dropped by the FormData
 * constructor automatically.
 *
 * Top-level fieldset stays disabled until the disclosure is expanded; this
 * means no engraving properties are submitted when the customer leaves the
 * accordion closed.
 *
 * @extends Component
 */
class EngravingWidget extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.root = this.querySelector('[data-engraving-root]');
    this.typeRadios = Array.from(this.querySelectorAll('input[name="properties[Gravur-Typ]"]'));
    this.typeBlocks = Array.from(this.querySelectorAll('[data-engraving-block]'));
    this.positionBlock = this.querySelector('[data-engraving-position-block]');
    this.summaryEl = this.querySelector('[data-engraving-summary]');
    this.summaryList = this.querySelector('[data-engraving-summary-list]');
    this.summaryEmpty = this.querySelector('[data-engraving-summary-empty]');
    this.statusEl = this.querySelector('[data-engraving-status]');
    this.errorEl = this.querySelector('[data-engraving-error]');
    this.fileInput = this.querySelector('input[type="file"][data-engraving-input="logo-file"]');
    this.fileButton = this.querySelector('[data-engraving-file-button]');
    this.fileFeedback = this.querySelector('[data-engraving-file-feedback]');
    this.fileName = this.querySelector('[data-engraving-file-name]');
    this.fileSize = this.querySelector('[data-engraving-file-size]');
    this.fileRemove = this.querySelector('[data-engraving-file-remove]');
    this.disclosureTrigger = this.querySelector('[ref="disclosureTrigger"]');

    if (!this.root || !this.typeRadios.length) return;

    this._bindEvents();
    this._observeDisclosure();
    this._syncActiveBlock();
    this._updateSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disclosureObserver?.disconnect();
    this.parentForm?.removeEventListener('formdata', this._onFormDataBound);
    this.parentForm?.removeEventListener('submit', this._onSubmitBound);
    document.removeEventListener('cart:update', this._onCartAddBound);
    const atcButton = this.parentForm?.querySelector('[data-testid="standalone-add-to-cart"], button[type="submit"][name="add"]');
    if (atcButton && this._onAtcClickBound) {
      atcButton.removeEventListener('click', this._onAtcClickBound);
    }
  }

  _bindEvents() {
    this.typeRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        this._syncActiveBlock();
        this._updateSummary();
        this._clearError();
      });
    });

    this.addEventListener('input', this._onInput);
    this.addEventListener('keydown', this._onKeydown);

    if (this.fileInput) {
      this.fileInput.addEventListener('change', this._onFileChange);
    }
    if (this.fileRemove) {
      this.fileRemove.addEventListener('click', this._onFileRemove);
    }

    this.parentForm = this.closest('form');
    if (this.parentForm) {
      this._onFormDataBound = this._onFormData.bind(this);
      this._onSubmitBound = this._onSubmit.bind(this);
      this.parentForm.addEventListener('formdata', this._onFormDataBound);
      this.parentForm.addEventListener('submit', this._onSubmitBound, true);
    }

    // Bundle id refreshed per submit. Stored on the widget so the formdata
    // handler can stamp the main item and the cart:update listener can pair
    // up the upcharge item.
    this._bundleId = null;
    this._pendingUpcharge = null;
    // Tracks ATC click → formdata → cart:update sequence. Only a click that
    // we observed in this tick is allowed to chain into an upcharge add.
    // Without this gate, any later formdata event (e.g. the section morph
    // that runs after cart removal) would re-arm _pendingUpcharge and a
    // stray cart:update fires another upcharge POST.
    this._atcClickAllowsUpcharge = false;

    this._onCartAddBound = this._onCartAdd.bind(this);
    document.addEventListener('cart:update', this._onCartAddBound);

    // Mark the next formdata as "user-initiated submit". After consumption,
    // any further formdata events (without a fresh click) won't re-arm the
    // upcharge.
    this._onAtcClickBound = () => {
      this._atcClickAllowsUpcharge = true;
    };
    const atcButton = this.parentForm?.querySelector('[data-testid="standalone-add-to-cart"], button[type="submit"][name="add"]');
    if (atcButton) {
      atcButton.addEventListener('click', this._onAtcClickBound);
    }
  }

  _observeDisclosure() {
    if (!this.disclosureTrigger) return;
    this._disclosureObserver = new MutationObserver(() => {
      const expanded = this.disclosureTrigger.getAttribute('aria-expanded') === 'true';
      this.root.disabled = !expanded;
      if (!expanded) {
        this._clearError();
      }
    });
    this._disclosureObserver.observe(this.disclosureTrigger, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    // Initial sync
    const expanded = this.disclosureTrigger.getAttribute('aria-expanded') === 'true';
    this.root.disabled = !expanded;
  }

  _syncActiveBlock() {
    const active = this.typeRadios.find((r) => r.checked);
    const activeType = active?.dataset.engravingType ?? null;
    this.typeBlocks.forEach((block) => {
      const isActive = block.dataset.engravingBlock === activeType;
      block.disabled = !isActive;
      block.hidden = !isActive;
    });
  }

  _onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;

    // Strip newlines on single-line text inputs (copy-paste safety)
    if (input.dataset.engravingInput === 'text-single' && /[\r\n]/.test(input.value)) {
      input.value = input.value.replace(/[\r\n]+/g, ' ');
    }

    this._updateCounter(input);
    this._updateSummary();
    this._clearError();
  };

  _onKeydown = (event) => {
    // Prevent newline entry in single-line text fields
    if (event.key === 'Enter' && event.target?.dataset?.engravingInput === 'text-single') {
      event.preventDefault();
    }
  };

  _updateCounter(input) {
    const counterKey = input.dataset.engravingInput;
    if (!counterKey) return;
    const counter = this.querySelector(`[data-engraving-counter="${counterKey}"]`);
    if (!counter) return;
    const max = parseInt(counter.dataset.max, 10);
    const current = input.value.length;
    counter.textContent = `${current} / ${max}`;
    counter.classList.toggle('engraving-widget__counter--near-limit', current >= Math.floor(max * 0.8));
    counter.classList.toggle('engraving-widget__counter--at-limit', current >= max);
    this._announceCounterThreshold(current, max);
  }

  _announceCounterThreshold(current, max) {
    if (!this.statusEl) return;
    const remaining = max - current;
    if (current >= max) {
      this.statusEl.textContent = this._t('engraving.counter.at_limit');
    } else if (remaining <= 5 && remaining >= 0) {
      this.statusEl.textContent = this._t('engraving.counter.near_limit', { remaining });
    } else if (this.statusEl.textContent) {
      this.statusEl.textContent = '';
    }
  }

  _onFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      this._hideFileFeedback();
      this._updateSummary();
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowedExt = ['svg', 'png', 'jpg', 'jpeg'];

    // HEIC reject (iOS Photos sometimes serves HEIC even if not in accept)
    if (file.type === 'image/heic' || file.type === 'image/heif' || ext === 'heic' || ext === 'heif') {
      this._showError(this._t('engraving.errors.logo_heic'));
      event.target.value = '';
      this._hideFileFeedback();
      return;
    }

    if (!allowedExt.includes(ext)) {
      this._showError(this._t('engraving.errors.logo_format'));
      event.target.value = '';
      this._hideFileFeedback();
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this._showError(this._t('engraving.errors.logo_too_large'));
      event.target.value = '';
      this._hideFileFeedback();
      return;
    }

    this._showFileFeedback(file);
    this._clearError();
    this._updateSummary();
  };

  _onFileRemove = (event) => {
    event.preventDefault();
    if (this.fileInput) this.fileInput.value = '';
    this._hideFileFeedback();
    this._updateSummary();
  };

  _showFileFeedback(file) {
    if (!this.fileFeedback) return;
    if (this.fileName) this.fileName.textContent = file.name;
    if (this.fileSize) this.fileSize.textContent = this._formatBytes(file.size);
    this.fileFeedback.hidden = false;
    if (this.fileButton) this.fileButton.hidden = true;
  }

  _hideFileFeedback() {
    if (!this.fileFeedback) return;
    this.fileFeedback.hidden = true;
    if (this.fileButton) this.fileButton.hidden = false;
  }

  _formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  _updateSummary() {
    if (!this.summaryList || !this.summaryEmpty) return;

    const activeRadio = this.typeRadios.find((r) => r.checked);
    if (!activeRadio) {
      this.summaryList.innerHTML = '';
      this.summaryList.hidden = true;
      this.summaryEmpty.hidden = false;
      return;
    }

    const rows = [];
    rows.push({ key: this._t('engraving.summary.type'), value: activeRadio.value });

    const activeType = activeRadio.dataset.engravingType;
    if (activeType === 'single-line') {
      const v = this.querySelector('[data-engraving-input="text-single"]')?.value?.trim();
      if (v) rows.push({ key: this._t('engraving.summary.text'), value: v });
    } else if (activeType === 'multi-line') {
      const lines = [
        this.querySelector('[data-engraving-input="text-multi-1"]')?.value?.trim(),
        this.querySelector('[data-engraving-input="text-multi-2"]')?.value?.trim(),
        this.querySelector('[data-engraving-input="text-multi-3"]')?.value?.trim(),
      ].filter(Boolean);
      if (lines.length) rows.push({ key: this._t('engraving.summary.text'), value: lines.join(' / ') });
    } else if (activeType === 'logo') {
      const file = this.fileInput?.files?.[0];
      if (file) rows.push({ key: this._t('engraving.summary.logo'), value: file.name });
    }

    const position = this.querySelector('[data-engraving-input="position"]')?.value?.trim();
    if (position) rows.push({ key: this._t('engraving.summary.position'), value: position });

    const phone = this.querySelector('[data-engraving-input="phone"]')?.value?.trim();
    if (phone) rows.push({ key: this._t('engraving.summary.phone'), value: phone });

    this.summaryList.innerHTML = rows
      .map(({ key, value }) => `<dt>${this._escapeHtml(key)}</dt><dd>${this._escapeHtml(value)}</dd>`)
      .join('');
    this.summaryList.hidden = rows.length === 0;
    this.summaryEmpty.hidden = rows.length > 0;
  }

  _onSubmit(event) {
    // Only validate if widget is active (disclosure open)
    if (this.root.disabled) {
      this._pendingUpcharge = null;
      return;
    }

    const activeRadio = this.typeRadios.find((r) => r.checked);
    if (!activeRadio) {
      this._failValidation(this._t('engraving.errors.type_required'), this.typeRadios[0]);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const activeType = activeRadio.dataset.engravingType;
    if (activeType === 'single-line') {
      const input = this.querySelector('[data-engraving-input="text-single"]');
      if (!input.value.trim()) {
        this._failValidation(this._t('engraving.errors.text_required'), input);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    } else if (activeType === 'multi-line') {
      const inputs = [1, 2, 3].map((i) => this.querySelector(`[data-engraving-input="text-multi-${i}"]`));
      if (!inputs.some((i) => i?.value?.trim())) {
        this._failValidation(this._t('engraving.errors.text_required'), inputs[0]);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    } else if (activeType === 'logo') {
      if (!this.fileInput?.files?.length) {
        this._failValidation(this._t('engraving.errors.logo_required'), this.fileInput);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    }

    const positionInput = this.querySelector('[data-engraving-input="position"]');
    if (!positionInput?.value?.trim()) {
      this._failValidation(this._t('engraving.errors.position_required'), positionInput);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    // Upcharge bookkeeping happens in `formdata` since the ATC button never
    // fires a native submit (product-form.js intercepts the click).
  }

  _onFormData(event) {
    if (this.root.disabled) {
      this._pendingUpcharge = null;
      this._bundleId = null;
      return;
    }
    // Only formdata events that came from a user-initiated ATC click should
    // arm the upcharge. Other formdata events (e.g. from theme section
    // morphs after a cart change) must not re-trigger an upcharge add.
    const userInitiated = this._atcClickAllowsUpcharge;
    this._atcClickAllowsUpcharge = false;
    const fd = event.formData;

    // NFC-normalize all engraving text properties so umlauts survive downstream tools intact
    for (const key of [
      'properties[Gravur-Text]',
      'properties[Gravur-Text-Z1]',
      'properties[Gravur-Text-Z2]',
      'properties[Gravur-Text-Z3]',
      'properties[Gravur-Position]',
      'properties[Gravur-Telefon]',
    ]) {
      const value = fd.get(key);
      if (typeof value === 'string' && value.length) {
        fd.set(key, value.normalize('NFC'));
      }
    }
    // Drop empty optional phone so it doesn't clutter the cart line
    if (fd.get('properties[Gravur-Telefon]') === '') {
      fd.delete('properties[Gravur-Telefon]');
    }

    // Decide upcharge here because the native `submit` event never fires
    // (product-form.js handles the ATC click and submits via fetch only).
    const upchargeVariantId = parseInt(this.dataset.upchargeVariantId, 10);
    const priceText = parseInt(this.dataset.priceText, 10) || 0;
    const priceLogo = parseInt(this.dataset.priceLogo, 10) || 0;
    const activeRadio = this.typeRadios.find((r) => r.checked);
    const priceSource = activeRadio?.dataset?.engravingPriceSource;
    const upchargeCents = priceSource === 'logo' ? priceLogo : priceText;

    if (userInitiated && upchargeVariantId && upchargeCents > 0) {
      this._bundleId = `gravur-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      this._pendingUpcharge = { variantId: upchargeVariantId, quantity: upchargeCents };
      fd.set('properties[_bundle_id]', this._bundleId);
      fd.set('properties[_bundle_role]', 'main');
    } else {
      this._bundleId = null;
      this._pendingUpcharge = null;
    }
  }

  /**
   * After product-form.js successfully adds the main item, this handler
   * fires the upcharge as a second /cart/add.js call and then refreshes
   * the cart sections so the cart drawer reflects both lines.
   */
  async _onCartAdd(event) {
    if (!this._pendingUpcharge) return;
    // Ignore our own re-dispatch
    if (event.detail?.data?.source === 'engraving-upcharge') return;
    // CartAddEvent.sourceId is the variant id, not the form id — match by productId instead
    const productId = event.detail?.data?.productId;
    const matchesOurProduct = !productId || productId === this.dataset.productId;
    if (!matchesOurProduct) return;
    if (event.detail?.data?.didError) {
      this._pendingUpcharge = null;
      this._bundleId = null;
      return;
    }

    const { variantId, quantity } = this._pendingUpcharge;
    const bundleId = this._bundleId;
    this._pendingUpcharge = null;
    this._bundleId = null;

    try {
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
              properties: {
                _bundle_id: bundleId,
                _bundle_role: 'upcharge',
              },
            },
          ],
        }),
      });
      if (!addResp.ok) return;

      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'engraving-widget',
            data: { source: 'engraving-upcharge' },
          },
        })
      );
    } catch (err) {
      console.error('[engraving] upcharge add failed', err);
    }
  }

  _failValidation(message, focusTarget) {
    this._showError(message);
    if (focusTarget) {
      focusTarget.setAttribute('aria-invalid', 'true');
      try { focusTarget.focus({ preventScroll: false }); } catch (_) { /* noop */ }
    }
  }

  _showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  _clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
    this.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.setAttribute('aria-invalid', 'false'));
  }

  _escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Lookup translation strings. Locale JSON isn't directly accessible to JS,
   * so the Liquid layer pre-renders critical strings into data-* attributes
   * where needed. For everything else, we fall back to inline German defaults
   * that match locales/de.json. Worst case the user sees German on a
   * non-German storefront — acceptable for inline counter / validation
   * messages until i18n hand-off is wired up.
   */
  _t(key, vars = {}) {
    const fallbacks = {
      'engraving.counter.near_limit': `${vars.remaining ?? 0} Zeichen übrig`,
      'engraving.counter.at_limit': 'Maximale Zeichenzahl erreicht',
      'engraving.summary.type': 'Art',
      'engraving.summary.text': 'Text',
      'engraving.summary.logo': 'Logo',
      'engraving.summary.position': 'Position',
      'engraving.summary.phone': 'Telefon',
      'engraving.errors.type_required': 'Bitte wähle eine Gravur-Art.',
      'engraving.errors.text_required': 'Bitte gib einen Text ein.',
      'engraving.errors.position_required': 'Bitte beschreibe die Position auf dem Produkt.',
      'engraving.errors.logo_required': 'Bitte lade eine Datei hoch.',
      'engraving.errors.logo_too_large': 'Datei zu groß (max. 10 MB).',
      'engraving.errors.logo_format': 'Format nicht unterstützt. Erlaubt: SVG, PNG, JPG.',
      'engraving.errors.logo_heic': 'HEIC wird nicht unterstützt. Bitte JPG, PNG oder SVG verwenden.',
    };
    return fallbacks[key] ?? key;
  }
}

if (!customElements.get('engraving-widget')) {
  customElements.define('engraving-widget', EngravingWidget);
}
